import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, Sparkles, Smartphone, Zap, RefreshCw, ShieldCheck,
  Truck, Star, Leaf, Gem, CreditCard, BarChart3, Calendar, Check,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { PRODUCTS, productBySlug, formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import { useCurrency } from "@/lib/ecom/currency";
import { alternateLinkList } from "@/lib/i18n/locale";
import metalHero from "@/assets/products/custom-black-metal.webp";
import bambooHero from "@/assets/products/bamboo-hero.webp";
import whiteHero from "@/assets/products/white-hero.webp";
import blackHero from "@/assets/products/custom-black-pvc.webp";
import goldHero from "@/assets/products/gold-hero.webp";
import googleHero from "@/assets/products/google-review-hero.webp";

const HERO_OG = metalHero;

const PRODUCT_IMAGES: Record<string, string> = {
  metal: metalHero,
  bamboo: bambooHero,
  "white-pvc": whiteHero,
  "black-pvc": blackHero,
  "24kt-gold": goldHero,
  "google-review": googleHero,
};

export const Route = createFileRoute("/el/shop")({
  head: () => ({
    meta: [
      { title: "Αγορά επαγγελματικών καρτών NFC — Μέταλλο, μπαμπού & χρυσός | Banana" },
      { name: "description", content: "Κορυφαίες επαγγελματικές κάρτες NFC από μέταλλο, μπαμπού, χρυσό 24kt και PVC — καθώς και κάρτες Google Review. Ένα άγγιγμα μοιράζεται τα πάντα." },
      { property: "og:title", content: "Αγορά επαγγελματικών καρτών NFC — Μέταλλο, μπαμπού & χρυσός | Banana" },
      { property: "og:description", content: "Κορυφαίες επαγγελματικές κάρτες NFC με AI. Επιλέξτε υλικό — αγγίξτε για κοινοποίηση και μη χάσετε ποτέ ξανά καμία επαφή." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "el_GR" },
      { property: "og:url", content: "https://banana.cards/el/shop" },
      { property: "og:image", content: HERO_OG },
      { property: "twitter:card", content: "summary_large_image" },
      { property: "twitter:image", content: HERO_OG },
    ],
    links: alternateLinkList("/shop", "el"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Store",
          name: "Κατάστημα Banana Cards",
          url: "https://banana.cards/el/shop",
          description: "Κορυφαίες επαγγελματικές κάρτες NFC με AI και κάρτες Google Review.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "ItemList",
          name: "Κατάστημα Banana Cards — Επαγγελματικές κάρτες NFC",
          numberOfItems: PRODUCTS.length,
          itemListElement: PRODUCTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://banana.cards/el${p.href}`,
            item: {
              "@type": "Product",
              "@id": `https://banana.cards/el${p.href}#product`,
              name: p.name,
              sku: p.sku,
              mpn: p.sku,
              brand: { "@type": "Brand", name: "Banana Cards" },
              category: "Επαγγελματικές κάρτες",
              image: PRODUCT_IMAGES[p.slug]
                ? `https://banana.cards${PRODUCT_IMAGES[p.slug]}`
                : undefined,
              url: `https://banana.cards/el${p.href}`,
              offers: {
                "@type": "Offer",
                priceCurrency: "USD",
                price: p.prices.US,
                availability: "https://schema.org/InStock",
                url: `https://banana.cards/el${p.href}`,
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://banana.cards/el/" },
            { "@type": "ListItem", position: 2, name: "Κατάστημα", item: "https://banana.cards/el/shop" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://banana.cards/el/shop#collection",
          name: "Συλλογή επαγγελματικών καρτών NFC",
          url: "https://banana.cards/el/shop",
          description: "Κορυφαίες επαγγελματικές κάρτες NFC από μέταλλο, μπαμπού, χρυσό 24kt, PVC και κάρτες Google Review.",
          isPartOf: { "@type": "WebSite", name: "Banana Cards", url: "https://banana.cards/el/" },
          about: { "@id": "https://banana.cards/el/shop#collection-items" },
        }),
      },
    ],
  }),
  component: ShopIndex,
});

type CardCopy = {
  slug: string;
  image: string;
  tagline: string;
  blurb: string;
  badge?: string;
  accent: string;
  bullets: string[];
  Icon: typeof Sparkles;
};

const COPY: CardCopy[] = [
  {
    slug: "metal",
    image: metalHero,
    tagline: "Ανοξείδωτο ατσάλι με μαύρη ηλεκτροστατική βαφή",
    blurb:
      "Σχεδιασμένη για να εντυπωσιάζει. Λογότυπο χαραγμένο με λέιζερ, ενσωματωμένο NFC, εγγύηση εφ’ όρου ζωής.",
    badge: "Η πιο δημοφιλής",
    accent: "bg-foreground text-background",
    bullets: ["Πυρήνας από ανοξείδωτο ατσάλι", "Λογότυπο χαραγμένο με λέιζερ", "Εγγύηση εφ’ όρου ζωής"],
    Icon: Gem,
  },
  {
    slug: "24kt-gold",
    image: goldHero,
    tagline: "Επιχρύσωση 24kt που αναδεικνύει το εμπορικό σας σήμα",
    blurb:
      "Η κάρτα που ξεχωρίζει. Γνήσια επιχρύσωση 24kt με εξατομικευμένη χάραξη και ενσωματωμένη επικοινωνία συνέχειας με AI.",
    badge: "Πολυτελής",
    accent: "bg-[oklch(0.82_0.16_85)] text-black",
    bullets: ["Επιχρύσωση 24kt", "Χειροποίητο φινίρισμα", "Εξατομικευμένη καθοδήγηση εκκίνησης"],
    Icon: Sparkles,
  },
  {
    slug: "bamboo",
    image: bambooHero,
    tagline: "Μπαμπού βιώσιμης προέλευσης",
    blurb:
      "Η συνειδητή επιλογή. Ζεστή, φυσική και 100% βιοδιασπώμενη — χωρίς καμία έκπτωση στις λειτουργίες.",
    badge: "Οικολογική",
    accent: "bg-brand-lime text-brand-lime-foreground",
    bullets: ["Βιώσιμο μπαμπού", "Χάραξη στο χέρι", "Πρόγραμμα δενδροφύτευσης"],
    Icon: Leaf,
  },
  {
    slug: "white-pvc",
    image: whiteHero,
    tagline: "Γυαλιστερό λευκό PVC με το εμπορικό σας σήμα στο επίκεντρο",
    blurb:
      "Ο καθαρός καμβάς σας. Έγχρωμη εκτύπωση, με το λογότυπό σας μεγάλο και φωτεινό — σε τιμή φιλική προς τις νεοφυείς επιχειρήσεις.",
    badge: "Καλύτερη σχέση ποιότητας-τιμής",
    accent: "bg-background text-foreground border border-foreground/15",
    bullets: ["Έγχρωμη εκτύπωση", "Ελαφριά κατασκευή", "Apple & Google Wallet"],
    Icon: CreditCard,
  },
  {
    slug: "black-pvc",
    image: blackHero,
    tagline: "Μαύρο ματ PVC, κομψό και μοντέρνο",
    blurb:
      "Η διακριτική επιλογή. Βαθύ μαύρο φινίρισμα με ευκρινή εκτύπωση — διακριτική πολυτέλεια με αυτοπεποίθηση.",
    badge: "Η πιο δημοφιλής",
    accent: "bg-foreground text-background",
    bullets: ["Μαύρο ματ φινίρισμα", "Έγχρωμη εκτύπωση", "Apple & Google Wallet"],
    Icon: CreditCard,
  },
  {
    slug: "google-review",
    image: googleHero,
    tagline: "Κριτικές 5 αστέρων με ένα άγγιγμα",
    blurb:
      "Η κάρτα της ανάπτυξης. Ένα άγγιγμα στον πάγκο και ο πελάτης αφήνει κριτική — τόσο απλά.",
    badge: "Για τοπικές επιχειρήσεις",
    accent: "bg-blue-500 text-white",
    bullets: ["Απευθείας στη σελίδα κριτικών Google", "Δεν απαιτείται εφαρμογή", "Παρακολούθηση κάθε σάρωσης"],
    Icon: Star,
  },
];

function getCopy(slug: string) {
  return COPY.find((c) => c.slug === slug)!;
}

export default function ShopIndex() {
  const { country } = useCurrency();
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-60">
          <div className="absolute -top-24 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-brand-lime/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-16 md:px-6 md:pb-16 md:pt-24">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <Sparkles className="h-3 w-3" /> Η συλλογή
            </span>
            <h1 className="mt-5 max-w-4xl font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
              Επιλέξτε την κάρτα σας.<br className="hidden md:block" />
              <span className="italic text-muted-foreground">Όλα τα υπόλοιπα, με ένα άγγιγμα.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-balance text-[15px] leading-relaxed text-muted-foreground md:text-base">
              Πέντε υλικά. Μία πλατφόρμα. Κάθε κάρτα Banana συνδυάζει κορυφαία κατασκευή με το
              προφίλ AI του banana.cards, CRM, κρατήσεις και επικοινωνία συνέχειας — έτοιμα για χρήση, χωρίς εφαρμογή.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a
                href="#collection"
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90"
              >
                Εξερευνήστε τη συλλογή <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/el/pricing"
                className="inline-flex items-center gap-2 rounded-xl border border-foreground/15 px-6 py-3 text-sm font-bold text-foreground transition hover:bg-muted"
              >
                Δείτε τα προγράμματα της πλατφόρμας
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Δωρεάν αποστολή παγκοσμίως άνω των $50</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Εγγύηση υλικού εφ’ όρου ζωής</span>
              <span className="inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> 4.9 · 2,100+ κριτικές</span>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTION */}
      <section id="collection" className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-20">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Όλες οι κάρτες</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Ολόκληρη η συλλογή Banana Cards</h2>
          </div>
          <p className="hidden max-w-sm text-sm text-muted-foreground md:block">
            Κάθε κάρτα αποστέλλεται έτοιμη για χρήση με ένα άγγιγμα — συνδέεται με το δωρεάν προφίλ σας στο banana.cards σε λιγότερο από 60 δευτερόλεπτα.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {COPY.map((c, i) => {
            const p = productBySlug(c.slug);
            if (!p) return null;
            const fromPrice = getProductBasePrice(p.slug, country.code);
            return (
              <Link
                key={c.slug}
                to={`/el${p.href}` as "/shop/metal"}
                className="group relative flex flex-col overflow-hidden rounded-3xl bg-card transition hover:-translate-y-0.5"
              >
                <div className="relative aspect-[5/4] overflow-hidden bg-background">
                  <img
                    src={c.image}
                    alt={p.name}
                    loading={i < 2 ? "eager" : "lazy"}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                  />
                  {c.badge && (
                    <span className={`absolute left-4 top-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${c.accent}`}>
                      <c.Icon className="h-3 w-3" /> {c.badge}
                    </span>
                  )}
                  <span className="absolute right-4 top-4 rounded-full bg-background/90 px-3 py-1 text-[11px] font-bold tabular-nums text-foreground backdrop-blur">
                    Από {formatMoney(fromPrice, country)}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{c.tagline}</p>
                  <h3 className="mt-1.5 font-display text-2xl leading-tight md:text-3xl">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.blurb}</p>

                  <ul className="mt-4 space-y-1.5">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[13px] text-foreground/80">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex items-center justify-between border-t border-foreground/10 pt-4">
                    <span className="text-sm font-semibold text-foreground">Αγοράστε {p.shortName}</span>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background transition group-hover:translate-x-0.5">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Πώς λειτουργεί</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Από το άνοιγμα της συσκευασίας στο πρώτο άγγιγμα σε 60 δευτερόλεπτα</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Επιλέξτε και εξατομικεύστε", d: "Επιλέξτε το υλικό σας, ανεβάστε το λογότυπό σας και εμείς κατασκευάζουμε την κάρτα.", Icon: Sparkles },
              { n: "02", t: "Αγγίξτε για ενεργοποίηση", d: "Αγγίξτε μία φορά το κινητό σας με την κάρτα για να ενεργοποιήσετε το δωρεάν προφίλ σας στο banana.cards.", Icon: Smartphone },
              { n: "03", t: "Μοιραστείτε και συνεχίστε την επικοινωνία", d: "Αγγίξτε για κοινοποίηση. Το AI καταγράφει κάθε επαφή και συντάσσει το επόμενο μήνυμα.", Icon: Zap },
            ].map((s) => (
              <div key={s.n} className="rounded-3xl bg-muted p-6">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Βήμα {s.n}</span>
                  <s.Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <h3 className="mt-3 font-display text-2xl leading-tight">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mb-10 flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Συγκρίνετε τα υλικά</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Ποια κάρτα Banana σας ταιριάζει;</h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Κάθε κάρτα λειτουργεί με την ίδια πλατφόρμα AI. Η διαφορά βρίσκεται στην πρώτη εντύπωση.
          </p>
        </div>

        <div className="overflow-x-auto rounded-3xl bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-foreground/10 text-left text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                <th className="px-5 py-3">Κάρτα</th>
                <th className="px-5 py-3">Ιδανική για</th>
                <th className="px-5 py-3">Αίσθηση</th>
                <th className="px-5 py-3">Από</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {[
                { slug: "metal",         best: "Ιδρυτές, επαγγελματίες πωλήσεων", feel: "Βαριά, πολυτελής, δροσερή στην αφή" },
                { slug: "24kt-gold",     best: "Πολυτέλεια, φιλοξενία",  feel: "Εντυπωσιακή, φινίρισμα καθρέφτη"   },
                { slug: "bamboo",        best: "Εμπορικά σήματα με οικολογική συνείδηση",   feel: "Ζεστή, φυσική, ελαφριά"       },
                { slug: "white-pvc",     best: "Ομάδες και νεοφυείς επιχειρήσεις",     feel: "Λιτή, με έγχρωμη εκτύπωση"    },
                { slug: "google-review", best: "Τοπικές επιχειρήσεις",     feel: "Φωτεινό PVC, έτοιμη για τον πάγκο"  },
              ].map((row) => {
                const p = productBySlug(row.slug)!;
                return (
                  <tr key={row.slug} className="border-b border-foreground/10 last:border-0">
                    <td className="px-5 py-4 font-semibold">{p.name}</td>
                    <td className="px-5 py-4 text-muted-foreground">{row.best}</td>
                    <td className="px-5 py-4 text-muted-foreground">{row.feel}</td>
                    <td className="px-5 py-4 tabular-nums">{formatMoney(getProductBasePrice(p.slug, country.code), country)}</td>
                    <td className="px-5 py-4 text-right">
                      <Link to={`/el${p.href}` as "/shop/metal"} className="inline-flex items-center gap-1 text-sm font-bold text-foreground hover:underline">
                        Αγοράστε <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      </section>

      {/* PLATFORM STRIP */}
      <section className="relative overflow-hidden bg-brand-dark text-background">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-12 md:px-6 md:py-24">
          <div className="md:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-background/70">
              Κάθε κάρτα περιλαμβάνει
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Μια πλατφόρμα AI, όχι απλώς μια κάρτα.
            </h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-background/80">
              Η κάρτα σας είναι η αφετηρία. Πίσω της βρίσκονται ένα CRM, ένας μηχανισμός επικοινωνίας συνέχειας με AI, έξυπνες κρατήσεις,
              ηλεκτρονικές υπογραφές συμβάσεων και αναλυτικά στοιχεία ομάδας — όλα περιλαμβάνονται από την πρώτη ημέρα.
            </p>
            <Link
              to="/el/pricing"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brand-lime px-5 py-2.5 text-sm font-bold text-brand-lime-foreground transition hover:opacity-90"
            >
              Δείτε τι περιλαμβάνεται <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {[
                { t: "Προφίλ AI",         d: "Πολύγλωσσο, με αυτόματη ενημέρωση", Icon: Sparkles },
                { t: "Καταγραφή επαφών",    d: "Κάθε άγγιγμα, καμία χαμένη επαφή",        Icon: RefreshCw },
                { t: "Επικοινωνία συνέχειας με AI",      d: "Προσχέδια σε δευτερόλεπτα",           Icon: Zap },
                { t: "Κρατήσεις",           d: "Περιλαμβάνονται ημερολόγιο και βιντεοκλήσεις",    Icon: Calendar },
                { t: "Αναλυτικά στοιχεία",          d: "Παρακολουθήστε κάθε αλληλεπίδραση",      Icon: BarChart3 },
                { t: "Διαχείριση ομάδας",      d: "Προσκλήσεις, εταιρική ταυτότητα, έλεγχος",        Icon: ShieldCheck },
              ].map((f) => (
                <div key={f.t} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <f.Icon className="h-4 w-4 text-brand-lime" />
                  <p className="mt-3 text-sm font-bold">{f.t}</p>
                  <p className="mt-0.5 text-[12px] text-background/70">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-6 md:grid-cols-4">
          {[
            { n: "2,100+",  l: "Κάρτες σε κυκλοφορία" },
            { n: "4.9 / 5", l: "Βαθμολογία πελατών" },
            { n: "60 δευτ.",  l: "Μέσος χρόνος ρύθμισης" },
            { n: "120+",    l: "Χώρες αποστολής" },
          ].map((s) => (
            <div key={s.l} className="rounded-3xl bg-muted p-6 text-center">
              <p className="font-display text-4xl">{s.n}</p>
              <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.l}</p>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Συχνές ερωτήσεις</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Ερωτήσεις για τις κάρτες</h2>
          </div>
          <div className="space-y-3">
            {[
              { q: "Χρειάζομαι εφαρμογή για να μοιραστώ την κάρτα μου;", a: "Όχι. Το NFC λειτουργεί εγγενώς σε κάθε κινητό iOS και Android που κατασκευάστηκε την τελευταία δεκαετία. Αγγίξτε μία φορά — το πλήρες προφίλ σας ανοίγει στο πρόγραμμα περιήγησης." },
              { q: "Μπορώ να παραγγείλω μία κάρτα για δοκιμή και να αναβαθμίσω αργότερα;", a: "Ναι. Ξεκινήστε με μία κάρτα για δοκιμή και, όταν είστε έτοιμοι να εξοπλίσετε την ομάδα σας, επιλέξτε πακέτο 3, 5 ή 10 καρτών με έκπτωση." },
              { q: "Μπορούν οι κάρτες να επαναπρογραμματιστούν;", a: "Η κάρτα σας δεν λήγει ποτέ και τα δεδομένα της μπορούν να ενημερωθούν ανά πάσα στιγμή από τον πίνακα ελέγχου σας — χωρίς επανεκτύπωση." },
              { q: "Πόσο χρόνο χρειάζεται η αποστολή;", a: "Οι εξατομικευμένες κάρτες αποστέλλονται σε 3–5 εργάσιμες ημέρες. Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50." },
              { q: "Τι γίνεται αν χάσω την κάρτα μου;", a: "Απενεργοποιήστε την αμέσως από τον πίνακα ελέγχου σας και παραγγείλτε αντικατάσταση με έκπτωση μέλους. Οι επαφές και το προφίλ σας παραμένουν ασφαλή." },
            ].map((f) => (
              <details key={f.q} className="group rounded-2xl bg-card p-5 open:shadow-sm">
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-[15px] font-semibold">
                  {f.q}
                  <span className="ml-4 inline-flex h-7 w-7 items-center justify-center rounded-full border border-foreground/15 text-muted-foreground transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-foreground p-8 text-background md:p-14">
          <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-brand-lime/40 blur-3xl" />
          <div className="relative grid items-center gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-background/60">Έτοιμοι όποτε είστε κι εσείς</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Η επόμενη πρώτη σας εντύπωση απέχει ένα άγγιγμα.
              </h2>
              <p className="mt-4 max-w-md text-[15px] text-background/70">
                Δωρεάν αποστολή άνω των $50. Εγγύηση υλικού εφ’ όρου ζωής. Επιστροφές εντός 30 ημερών χωρίς ερωτήσεις.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link to={"/el/shop/metal" as "/shop/metal"} className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:opacity-90">
                  Αγοράστε την πιο δημοφιλή κάρτα <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-background/10">
                  Μιλήστε με το τμήμα πωλήσεων
                </Link>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>
    </SiteLayout>
  );
}
